import { useEffect, useMemo, useState } from "react";
import { Modal, Input, Button, Alert, message, Radio, Select, Divider, Spin } from "antd";
import { CheckCircleOutlined } from "@ant-design/icons";
import { useMutation, useQuery } from "@apollo/client";
import ConnectTrackerScanner from "./ConnectTrackerScanner";
import { CONNECT_TRACKER, GET_USERS } from "../graphql/queries";
import { ConnectScanResult } from "../utils/connectScanParser";
import { PlaceWithDistance, User } from "../types";

type TrackerIdMode = "trackerId" | "caseId";
type LinkTargetType = "user" | "place";

interface LinkTrackerModalProps {
  open: boolean;
  onClose: () => void;
  places: PlaceWithDistance[];
}

export default function LinkTrackerModal({ open, onClose, places }: LinkTrackerModalProps) {
  const [showScanner, setShowScanner] = useState(false);
  const [scannedTrackerId, setScannedTrackerId] = useState<string | null>(null);
  const [scannedCaseId, setScannedCaseId] = useState<string | null>(null);
  const [idMode, setIdMode] = useState<TrackerIdMode>("caseId");
  const [manualIdValue, setManualIdValue] = useState("");
  const [linkType, setLinkType] = useState<LinkTargetType>("user");
  const [linkTargetId, setLinkTargetId] = useState<string | undefined>(undefined);

  const { data: usersData, loading: loadingUsers } = useQuery<{
    getUsers: { success: boolean; message?: string; users: User[] };
  }>(GET_USERS, {
    skip: !open,
  });

  const [connectTracker, { loading: submitting }] = useMutation<{
    connectTracker: { success: boolean; message: string };
  }>(CONNECT_TRACKER);

  useEffect(() => {
    if (!open) {
      setShowScanner(false);
      setScannedTrackerId(null);
      setScannedCaseId(null);
      setIdMode("caseId");
      setManualIdValue("");
      setLinkType("user");
      setLinkTargetId(undefined);
    }
  }, [open]);

  const userOptions = useMemo(() => {
    const list = usersData?.getUsers?.users || [];
    return list.map((user) => ({
      value: user.id,
      label: user.login_name ? `${user.name} (${user.login_name})` : user.name,
    }));
  }, [usersData?.getUsers?.users]);

  const placeOptions = useMemo(
    () =>
      places.map((place) => ({
        value: place.id,
        label: place.loc_name ? `${place.name} (${place.loc_name})` : place.name,
      })),
    [places]
  );

  const resolvedTrackerId =
    scannedTrackerId || (idMode === "trackerId" && !scannedCaseId ? manualIdValue.trim() : "");
  const resolvedCaseId =
    scannedCaseId || (idMode === "caseId" && !scannedTrackerId ? manualIdValue.trim() : "");
  const hasTrackerIdentifier = Boolean(resolvedTrackerId || resolvedCaseId);
  const canSubmit = hasTrackerIdentifier && Boolean(linkTargetId);

  const handleClose = () => {
    onClose();
  };

  const handleConnectScan = (result: ConnectScanResult) => {
    setShowScanner(false);

    switch (result.kind) {
      case "trackerId":
        setScannedTrackerId(result.trackerId);
        setScannedCaseId(null);
        setManualIdValue("");
        setIdMode("trackerId");
        message.success("Tracker ID doplněn ze skenu.");
        break;
      case "caseId":
        setScannedCaseId(result.caseId);
        setScannedTrackerId(null);
        setManualIdValue("");
        setIdMode("caseId");
        message.success("Case ID doplněn ze skenu.");
        break;
      case "user":
        setLinkType("user");
        setLinkTargetId(result.userId);
        message.success("Uživatel doplněn ze skenu.");
        break;
      case "place":
        setLinkType("place");
        setLinkTargetId(result.placeId);
        message.success("Místo doplněno ze skenu.");
        break;
    }
  };

  const handleClearTrackerScan = () => {
    setScannedTrackerId(null);
    setScannedCaseId(null);
  };

  const handleLinkTypeChange = (next: LinkTargetType) => {
    setLinkType(next);
    setLinkTargetId(undefined);
  };

  const handleSubmit = async () => {
    if (!canSubmit || !linkTargetId) {
      return;
    }

    const input: {
      trackerId?: string;
      caseId?: string;
      userId?: string;
      placeId?: string;
    } = {};

    if (scannedTrackerId) {
      input.trackerId = scannedTrackerId;
    } else if (scannedCaseId) {
      input.caseId = scannedCaseId;
    } else if (idMode === "trackerId") {
      input.trackerId = manualIdValue.trim();
    } else {
      input.caseId = manualIdValue.trim();
    }

    if (linkType === "user") {
      input.userId = linkTargetId;
    } else {
      input.placeId = linkTargetId;
    }

    try {
      const { data: res } = await connectTracker({ variables: { input } });
      if (res?.connectTracker?.success) {
        message.success(res.connectTracker.message || "Tracker byl propojen.");
        handleClose();
      } else {
        message.error(res?.connectTracker?.message || "Chyba při propojování trackeru.");
      }
    } catch (e) {
      console.error(e);
      message.error("Chyba při propojování trackeru.");
    }
  };

  const hasScannedIdentifier = Boolean(scannedTrackerId || scannedCaseId);

  return (
    <>
      <ConnectTrackerScanner
        visible={open && showScanner}
        onClose={() => setShowScanner(false)}
        onScan={handleConnectScan}
      />

      <Modal
        title="Propojit tracker"
        open={open && !showScanner}
        onCancel={handleClose}
        footer={[
          <Button key="cancel" onClick={handleClose}>
            Zrušit
          </Button>,
          <Button
            key="submit"
            type="primary"
            loading={submitting}
            disabled={!canSubmit}
            onClick={() => void handleSubmit()}
          >
            Propojit tracker
          </Button>,
        ]}
        destroyOnClose
        centered
      >
        <div style={{ marginBottom: 16 }}>
          <div style={{ marginBottom: 8, fontWeight: 500 }}>Identifikace trackeru</div>
          {scannedTrackerId ? (
            <Alert
              type="success"
              showIcon
              icon={<CheckCircleOutlined />}
              message="Tracker ID naskenován"
              description={
                <span style={{ wordBreak: "break-all", fontWeight: 600 }}>{scannedTrackerId}</span>
              }
              action={
                <Button size="small" onClick={handleClearTrackerScan}>
                  Změnit
                </Button>
              }
              style={{ marginBottom: 12 }}
            />
          ) : scannedCaseId ? (
            <Alert
              type="success"
              showIcon
              icon={<CheckCircleOutlined />}
              message="Case ID naskenován"
              description={
                <span style={{ wordBreak: "break-all", fontWeight: 600 }}>{scannedCaseId}</span>
              }
              action={
                <Button size="small" onClick={handleClearTrackerScan}>
                  Změnit
                </Button>
              }
              style={{ marginBottom: 12 }}
            />
          ) : null}

          {!hasScannedIdentifier && (
            <>
              <Button block onClick={() => setShowScanner(true)} style={{ marginBottom: 12 }}>
                Naskenovat QR
              </Button>
              <Divider plain style={{ margin: "12px 0" }}>
                nebo zadat ručně
              </Divider>
              <Radio.Group
                value={idMode}
                onChange={(e) => {
                  setIdMode(e.target.value as TrackerIdMode);
                  setManualIdValue("");
                }}
                style={{ marginBottom: 8 }}
              >
                <Radio value="caseId">Case ID</Radio>
                <Radio value="trackerId">Tracker ID</Radio>
              </Radio.Group>
              <Input
                value={manualIdValue}
                onChange={(e) => setManualIdValue(e.target.value)}
                placeholder={idMode === "trackerId" ? "Zadejte tracker ID (číslo)" : "Zadejte case ID"}
              />
            </>
          )}

          {hasScannedIdentifier ? (
            <Button block onClick={() => setShowScanner(true)} style={{ marginTop: 12 }}>
              Naskenovat další QR
            </Button>
          ) : null}
        </div>

        <div>
          <div style={{ marginBottom: 8, fontWeight: 500 }}>Propojit s</div>
          <Radio.Group
            value={linkType}
            onChange={(e) => handleLinkTypeChange(e.target.value as LinkTargetType)}
            style={{ marginBottom: 12 }}
          >
            <Radio value="user">Uživatel</Radio>
            <Radio value="place">Místo</Radio>
          </Radio.Group>

          {linkType === "user" ? (
            loadingUsers ? (
              <Spin />
            ) : (
              <Select
                showSearch
                allowClear
                optionFilterProp="label"
                placeholder="Vyberte uživatele"
                style={{ width: "100%" }}
                value={linkTargetId}
                onChange={setLinkTargetId}
                options={userOptions}
                notFoundContent={loadingUsers ? <Spin size="small" /> : "Žádní uživatelé"}
              />
            )
          ) : (
            <Select
              showSearch
              allowClear
              optionFilterProp="label"
              placeholder="Vyberte místo"
              style={{ width: "100%" }}
              value={linkTargetId}
              onChange={setLinkTargetId}
              options={placeOptions}
              notFoundContent="Žádná místa"
            />
          )}
        </div>
      </Modal>
    </>
  );
}
